import { useEffect, useRef, useState, useCallback } from "react";
import styles from "./FeedbackPlugin.module.css";
import Header from '../../Header/Header.jsx';
import SiteFooter from '../../Footer/Footer.jsx';

/* ════════════════════════════════════════════
   HERO CANVAS PARTICLES
════════════════════════════════════════════ */
function HeroCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;

    function resize() {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    }
    resize();
    window.addEventListener("resize", resize);

    const pts = Array.from({ length: 65 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.4 + 0.3,
      vx: (Math.random() - 0.5) * 0.28,
      vy: (Math.random() - 0.5) * 0.28,
      a: Math.random() * 0.55 + 0.18,
    }));

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pts.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,165,60,${p.a})`;
        ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 105) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(255,140,30,${0.11 * (1 - d / 105)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    }
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className={styles.heroCanvas} />;
}

/* ════════════════════════════════════════════
   INTERSECTION OBSERVER HOOK
════════════════════════════════════════════ */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, inView];
}

/* ════════════════════════════════════════════
   COUNTER ANIMATION HOOK
════════════════════════════════════════════ */
function useCounter(target, suffix = "", active = false) {
  const [value, setValue] = useState("0");

  useEffect(() => {
    if (!active) return;
    const dur = 1600;
    const start = Date.now();
    function tick() {
      const prog = Math.min((Date.now() - start) / dur, 1);
      const ease = 1 - Math.pow(1 - prog, 3);
      setValue(Math.round(ease * target) + suffix);
      if (prog < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }, [active, target, suffix]);

  return value;
}

/* ════════════════════════════════════════════
   MOCK SCREENS
════════════════════════════════════════════ */
function MockAdminScreen() {
  return (
    <div className={styles.mockWin}>
      <div className={styles.mockBar}>
        <div className={styles.mockDots}>
          <span className={styles.mockDot} style={{ background: "#ff5f56" }} />
          <span className={styles.mockDot} style={{ background: "#ffbd2e" }} />
          <span className={styles.mockDot} style={{ background: "#27c93f" }} />
        </div>
        <span className={styles.mockBarTitle}>
          WordPress Admin — Custom Feedback Rating
        </span>
      </div>
      <div className={styles.mockBody}>
        <div className={styles.mockSidebar}>
          {["Dashboard", "Feedback Rating", "Plugins", "Settings"].map((item) => (
            <div
              key={item}
              className={`${styles.mockSideItem} ${
                item === "Feedback Rating" ? styles.mockSideItemActive : ""
              }`}
            >
              {item}
            </div>
          ))}
        </div>
        <div className={styles.mockMain}>
          <div className={styles.mockMainHeader}>
            ⭐ Custom Feedback Rating{" "}
            <span className={styles.mockBadge2}>v1.7</span>
          </div>
          <div className={styles.mockTabs2}>
            {["Custom Widget", "CF7 Mode", "Statistics"].map((t, i) => (
              <span
                key={t}
                className={`${styles.mockTab2} ${i === 0 ? styles.mockTab2Active : ""}`}
              >
                {t}
              </span>
            ))}
          </div>
          <div className={styles.mockForm}>
            <div className={styles.mockRow2}>
              <div className={styles.mockLbl}>Widget Name</div>
              <div className={styles.mockInp}>Homepage Survey</div>
            </div>
            <div className={styles.mockRow2}>
              <div className={styles.mockLbl}>Options</div>
              <div className={styles.mockOpts}>
                {[
                  { emoji: "😊", label: "Excellent", count: 142 },
                  { emoji: "🙂", label: "Good", count: 89 },
                  { emoji: "😐", label: "Average", count: 31 },
                  { emoji: "😞", label: "Poor", count: 8 },
                ].map((opt) => (
                  <div key={opt.label} className={styles.mockOpt}>
                    <span className={styles.mockOptEmoji}>{opt.emoji}</span>
                    <span>{opt.label}</span>
                    <span className={styles.mockOptCount}>{opt.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.mockSc}>
              Shortcode:{" "}
              <code className={styles.mockScCode}>
                [cfr_custom_feedback id="5"]
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockStatsScreen() {
  const bars = [
    { name: "Very Satisfied", pct: 62, color: "#4ade80" },
    { name: "Satisfied", pct: 28, color: "#ffa040" },
    { name: "Dissatisfied", pct: 10, color: "#f87171" },
  ];

  return (
    <div className={styles.mockWin}>
      <div className={styles.mockBar}>
        <div className={styles.mockDots}>
          <span className={styles.mockDot} style={{ background: "#ff5f56" }} />
          <span className={styles.mockDot} style={{ background: "#ffbd2e" }} />
          <span className={styles.mockDot} style={{ background: "#27c93f" }} />
        </div>
        <span className={styles.mockBarTitle}>
          Statistics Panel — CF7 Feedback Mode
        </span>
      </div>
      <div className={styles.mockStatsBody}>
        <div className={styles.statTotal}>
          <span className={styles.statBig}>271</span>
          <span className={styles.statLbl2}>Total Responses</span>
        </div>
        <div className={styles.statBars}>
          {bars.map((b) => (
            <div key={b.name} className={styles.statRow}>
              <div className={styles.statMeta}>
                <span className={styles.statName}>{b.name}</span>
                <span className={styles.statPct} style={{ color: b.color }}>
                  {b.pct}%
                </span>
              </div>
              <div className={styles.barTrack}>
                <div
                  className={styles.barFill}
                  style={{ width: `${b.pct}%`, background: b.color }}
                />
              </div>
            </div>
          ))}
        </div>
        <button className={styles.mockResetBtn}>Reset All Counters</button>
      </div>
    </div>
  );
}

function MockPopupScreen() {
  return (
    <div className={styles.mockWin}>
      <div className={styles.mockBar}>
        <div className={styles.mockDots}>
          <span className={styles.mockDot} style={{ background: "#ff5f56" }} />
          <span className={styles.mockDot} style={{ background: "#ffbd2e" }} />
          <span className={styles.mockDot} style={{ background: "#27c93f" }} />
        </div>
        <span className={styles.mockBarTitle}>
          Frontend Widget — Dissatisfied Popup Flow
        </span>
      </div>
      <div className={styles.mockFrontendBody}>
        <div className={styles.feedbackWidget}>
          <p className={styles.widgetQ}>How was your experience?</p>
          <div className={styles.widgetBtns}>
            <button
              className={styles.wBtn}
              style={{ borderColor: "#4ade80", color: "#4ade80" }}
            >
              😊 Very Satisfied
            </button>
            <button
              className={styles.wBtn}
              style={{ borderColor: "#ffa040", color: "#ffa040" }}
            >
              🙂 Satisfied
            </button>
            <button
              className={`${styles.wBtn} ${styles.wBtnSelected}`}
              style={{
                borderColor: "#f87171",
                color: "#f87171",
                background: "rgba(248,113,113,.1)",
              }}
            >
              😞 Dissatisfied
            </button>
          </div>
        </div>
        <div className={styles.mockPopupWrap}>
          <div className={styles.popupInner}>
            <span className={styles.popupX}>✕</span>
            <p className={styles.popupTitle}>We're sorry to hear that!</p>
            <p className={styles.popupSub}>
              Please share your experience so we can improve.
            </p>
            <div className={styles.popupField}>Your Name</div>
            <div className={styles.popupField}>Your Email</div>
            <div className={`${styles.popupField} ${styles.popupTa}`}>
              What went wrong?
            </div>
            <button className={styles.popupSubmit}>Send Feedback</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const SCREENS = [
  { title: "Admin Widget Builder", desc: "Create and manage custom feedback widgets from a clean admin interface.", img: require("../../../images/feedback-1.png") },
  { title: "Live Statistics Panel", desc: "Per-option counts and percentage breakdowns with one-click reset.", img: require("../../../images/feedback-2.png") },
  { title: "Dissatisfied Popup", desc: "Unhappy visitors captured with an optional CF7 form popup.", img: require("../../../images/feedback-3.png") },
];

/* ════════════════════════════════════════════
   SECTION COMPONENTS
════════════════════════════════════════════ */

function Hero() {
  return (
    <section className={styles.hero} id="hero">
      <HeroCanvas />
      <div className={styles.heroOverlay} />
      <div className={`${styles.heroOrb} ${styles.heroOrb1}`} />
      <div className={`${styles.heroOrb} ${styles.heroOrb2}`} />

      <div className={styles.heroContent}>
        <div className={styles.heroBadge}>
          <span className={styles.badgeDot} />
          WordPress Plugin · Version 1.7
        </div>
        <h1 className={styles.heroTitle}>
          <span className={styles.heroLine1}>Custom Feedback</span>
          <span className={styles.heroLine2}>Rating</span>
          <span className={styles.heroLine3}>+ CF7</span>
        </h1>
        <p className={styles.heroSub}>
          Two powerful feedback modes in one lightweight plugin. Collect visitor
          sentiment with custom rating widgets or deep CF7-integrated forms — no
          bloat, no lock-in.
        </p>
        <div className={styles.heroActions}>
          <a href="#features" className={styles.btnPrimary}>
            Explore Features
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
          <a href="#how" className={styles.btnSecondary}>
            How It Works
          </a>
        </div>
        <div className={styles.heroMeta}>
          {[
            { val: "PHP 7.0+", label: "Minimum" },
            { val: "WP 6.9", label: "Tested Up To" },
            { val: "0 Tables", label: "DB Footprint" },
            { val: "OOP PHP", label: "Architecture" },
          ].map((m, i) => (
            <div key={m.label} style={{ display: "contents" }}>
              {i > 0 && <div className={styles.heroSep} />}
              <div className={styles.heroMetaItem}>
                <span className={styles.metaVal}>{m.val}</span>
                <span className={styles.metaLabel}>{m.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      
    </section>
  );
}

function FeatCard({ accent, tag, title, sub, items, shortcode, delay }) {
  const [ref, inView] = useInView();
  const isBlue = accent === "blue";

  return (
    <div
      ref={ref}
      className={`${styles.featCard} ${isBlue ? styles.featCardBlue : styles.featCardOrange} ${inView ? styles.in : ""}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className={styles.featInner}>
        <span className={`${styles.featTag} ${isBlue ? styles.featTagBlue : styles.featTagOrange}`}>
          {tag}
        </span>
        <h3 className={styles.featTitle}>{title}</h3>
        <p className={styles.featSub}>{sub}</p>
        <ul className={styles.featList}>
          {items.map((item) => (
            <li key={item.text} className={styles.featItem}>
              <span className={styles.featIcon}>{item.icon}</span>
              <span className={styles.featText}>{item.text}</span>
            </li>
          ))}
        </ul>
        <div className={`${styles.codeBlock} ${isBlue ? styles.codeBlockBlue : styles.codeBlockOrange}`}>
          <span className={styles.codeLbl}>Shortcode</span>
          <code className={isBlue ? styles.codeValBlue : styles.codeValOrange}>
            {shortcode}
          </code>
        </div>
      </div>
      <div className={styles.featGlow} />
    </div>
  );
}

function Features() {
  return (
    <section className={styles.features} id="features">
      <div className={styles.featuresContainer}>
        <div className={styles.featuresHeader}>
          <div className={styles.sectionTag}>What It Does</div>
          <h2 className={styles.sectionHeading}>
            Two Modes.{" "}
            <span className={styles.gradientText}>One Plugin.</span>
          </h2>
          <p className={styles.featuresDesc}>
            Choose standalone custom widgets or deep CF7 integration — or use
            both on the same site simultaneously.
          </p>
        </div>
        <div className={styles.featuresGrid}>
          <FeatCard
            accent="orange"
            tag="Mode A"
            title="Custom Feedback Widget"
            sub="Standalone — no dependencies required"
            items={[
              { icon: "🎛️", text: "Build fully custom rating widgets with any number of options" },
              { icon: "🖼️", text: "Upload custom images per option via the WordPress Media Library" },
              { icon: "⚡", text: "Auto-generates shortcode to embed on any page or post" },
              { icon: "📊", text: "Tracks per-option click counts with live percentage display" },
              { icon: "🔄", text: "Reset all counters at any time directly from the admin panel" },
            ]}
            shortcode='[cfr_custom_feedback id="42"]'
            delay={0}
          />
          <FeatCard
            accent="blue"
            tag="Mode B"
            title="CF7 Feedback"
            sub="Requires Contact Form 7"
            items={[
              { icon: "😊", text: "Three fixed sentiments: Very Satisfied, Satisfied, Dissatisfied" },
              { icon: "✅", text: "Satisfied clicks silently record count and show a thank-you message" },
              { icon: "💬", text: "Dissatisfied triggers a popup with a detailed CF7 feedback form" },
              { icon: "📈", text: "Per-rating statistics with full reset support from admin panel" },
              { icon: "🔗", text: "Simple shortcode accepts CF7 form ID as a parameter" },
            ]}
            shortcode='[feedback_rating form_id="123"]'
            delay={0.18}
          />
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { num: "01", title: "Install & Activate", desc: "Drop the plugin into your WordPress installation. No configuration wizards, no onboarding screens — it just works." },
    { num: "02", title: "Build Your Widget", desc: "Use the admin panel to add rating options, upload images from the Media Library, and customise everything to your needs." },
    { num: "03", title: "Embed Shortcode", desc: "Copy the auto-generated shortcode and paste it into any page, post, or widget area across your site." },
    { num: "04", title: "Monitor Stats", desc: "Watch the live statistics panel fill with per-option click counts and percentage breakdowns in real time." },
  ];

  const techCards = [
    { label: "Admin Class", val: "Feedback_Admin", desc: "Handles all WordPress admin UI, settings pages, and Media Library integration for image uploads." },
    { label: "Ajax Class", val: "Feedback_Ajax", desc: "All AJAX actions are nonce-verified and capability-checked against manage_options for maximum security." },
    { label: "Shortcode Class", val: "Feedback_Shortcode", desc: "Renders frontend widgets and CF7-integrated forms with the dissatisfied popup flow." },
  ];

  return (
    <section className={styles.how} id="how">
      <div className={styles.howContainer}>
        <div className={styles.howHeader}>
          <div className={styles.sectionTag}>How It Works</div>
          <h2 className={styles.sectionHeading}>
            Up and running{" "}
            <span className={styles.gradientText}>in minutes.</span>
          </h2>
        </div>

        <div className={styles.stepsRow}>
          {steps.map((s, i) => (
            <StepCard key={s.num} {...s} delay={i * 0.13} />
          ))}
        </div>

        <div className={styles.techArch}>
          <h3 className={styles.techArchTitle}>OOP Architecture</h3>
          <p className={styles.techArchSub}>Three focused classes. Zero spaghetti.</p>
          <div className={styles.techCards}>
            {techCards.map((tc, i) => (
              <TechCard key={tc.val} {...tc} delay={i * 0.11} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({ num, title, desc, delay }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`${styles.step} ${inView ? styles.in : ""}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className={styles.stepNum}>{num}</div>
      <h4 className={styles.stepTitle}>{title}</h4>
      <p className={styles.stepDesc}>{desc}</p>
    </div>
  );
}

function TechCard({ label, val, desc, delay }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`${styles.techCard} ${inView ? styles.in : ""}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <span className={styles.tcLabel}>{label}</span>
      <code className={styles.tcVal}>{val}</code>
      <p className={styles.tcDesc}>{desc}</p>
    </div>
  );
}

function Screenshots() {
  const [active, setActive] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [layoutRef, layoutInView] = useInView();

  const handleTab = useCallback((i) => {
    setActive(i);
    setAnimKey((k) => k + 1);
  }, []);

  return (
    <section className={styles.screenshots} id="screenshots">
      <div className={styles.ssContainer}>
        <div className={styles.ssHeader}>
          <div className={styles.sectionTag}>Screenshots</div>
          <h2 className={styles.sectionHeading}>
            See it in <span className={styles.gradientText}>action.</span>
          </h2>
        </div>

        <div
          ref={layoutRef}
          className={`${styles.ssLayout} ${layoutInView ? styles.in : ""}`}
        >
          <div className={styles.ssTabs}>
            {SCREENS.map((s, i) => (
              <button
                key={s.title}
                className={`${styles.ssTab} ${active === i ? styles.active : ""}`}
                onClick={() => handleTab(i)}
              >
                <span className={styles.ssTabNum}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <div className={styles.ssTabTitle}>{s.title}</div>
                  <div className={styles.ssTabDesc}>{s.desc}</div>
                </div>
              </button>
            ))}
          </div>

          <div key={animKey} className={styles.ssPreview}>
            <img src={SCREENS[active].img} alt={SCREENS[active].title} className={styles.ssPreviewImg} />
          </div>
        </div>
      </div>
    </section>
  );
}

function TechStack() {
  const techs = [
    { icon: "🐘", name: "PHP", sub: "7.0+", desc: "Server-side logic with three focused OOP classes" },
    { icon: "⚙️", name: "WordPress", sub: "API", desc: "Options API, hooks, nonces, capabilities" },
    { icon: "⚡", name: "jQuery", sub: "AJAX", desc: "Frontend interactions and AJAX request handling" },
    { icon: "🔁", name: "AJAX", sub: "REST", desc: "Nonce-verified, capability-checked async actions" },
    { icon: "🌍", name: "i18n", sub: "Ready", desc: "Translation-ready with text domain support" },
    { icon: "📋", name: "CF7", sub: "Optional", desc: "Contact Form 7 for deep feedback integration" },
  ];

  const highlights = [
    { icon: "🔒", label: "Secure", desc: "Every AJAX action nonce-verified and manage_options permission-checked" },
    { icon: "🧹", label: "Clean Uninstall", desc: "All plugin data wiped on removal — absolutely zero footprint left behind" },
    { icon: "🗄️", label: "No Custom Tables", desc: "All data stored in WordPress Options API — no migrations ever needed" },
    { icon: "🖼️", label: "Media Library", desc: "Native WP Media integration for seamless per-option image uploads" },
  ];

  return (
    <section className={styles.techstack} id="techstack">
      <div className={styles.tsContainer}>
        <div className={styles.tsHeader}>
          <div className={styles.sectionTag}>Tech Stack</div>
          <h2 className={styles.sectionHeading}>
            Built with <span className={styles.gradientText}>precision.</span>
          </h2>
          <p className={styles.tsDesc}>
            Lightweight, dependency-aware, and production-ready out of the box.
          </p>
        </div>

        <div className={styles.tsGrid}>
          {techs.map((t, i) => (
            <TsCard key={t.name} {...t} delay={i * 0.07} />
          ))}
        </div>

        <div className={styles.tsHighlights}>
          {highlights.map((h, i) => (
            <TsHighlight key={h.label} {...h} delay={i * 0.09} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TsCard({ icon, name, sub, desc, delay }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`${styles.tsCard} ${inView ? styles.in : ""}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <span className={styles.tsIcon}>{icon}</span>
      <div className={styles.tsName}>{name}</div>
      <div className={styles.tsSub}>{sub}</div>
      <p className={styles.tsDesc2}>{desc}</p>
    </div>
  );
}

function TsHighlight({ icon, label, desc, delay }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`${styles.tsHl} ${inView ? styles.in : ""}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <span className={styles.hlIcon}>{icon}</span>
      <div>
        <div className={styles.hlLabel}>{label}</div>
        <div className={styles.hlDesc}>{desc}</div>
      </div>
    </div>
  );
}

function StatBox({ target, suffix, label, sub }) {
  const [ref, inView] = useInView(0.5);
  const value = useCounter(target, suffix, inView);
  return (
    <div ref={ref} className={styles.statBox}>
      <span className={styles.statCounter}>{value}</span>
      <div className={styles.statBoxLabel}>{label}</div>
      <div className={styles.statBoxSub}>{sub}</div>
    </div>
  );
}

function Stats() {
  const stats = [
    { target: 3, suffix: "", label: "Core OOP Classes", sub: "Admin · Ajax · Shortcode" },
    { target: 0, suffix: "", label: "Custom DB Tables", sub: "Uses WordPress Options API" },
    { target: 2, suffix: "", label: "Feedback Modes", sub: "Standalone & CF7-integrated" },
    { target: 6, suffix: ".9", label: "WP Tested To", sub: "Always up to date" },
  ];

  return (
    <section className={styles.statsSection} id="stats">
      <div className={styles.statsContainer}>
        <div className={styles.statsGrid}>
          {stats.map((s) => (
            <StatBox key={s.label} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════
   FONT LOADER
════════════════════════════════════════════ */
function FontLoader() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500&display=swap";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);
  return null;
}

/* ════════════════════════════════════════════
   ROOT COMPONENT
════════════════════════════════════════════ */
export default function CFRPlugin() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.root}>
        <FontLoader />
        <Hero />
        <div className={styles.sectionDivider} />
        <Features />
        <div className={styles.sectionDivider} />
        <HowItWorks />
        <div className={styles.sectionDivider} />
        <Screenshots />
        <div className={styles.sectionDivider} />
        <TechStack />
        <Stats />
      </div>
      <SiteFooter />
    </>
  );
}